/**
 * Retry utility for handling API calls with automatic retries
 * Implements exponential backoff for better reliability
 */

/**
 * Execute an async function with automatic retries
 * @param {Function} fn - The async function to retry
 * @param {Object} options - Retry configuration
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff (default: 2)
 * @param {Function} options.onRetry - Optional callback when retrying
 * @returns {Promise} Result of the function or throws error after all retries
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    onRetry = null
  } = options;

  let lastError;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }

      // Calculate next delay with exponential backoff
      const currentDelay = Math.min(delay, maxDelay);
      
      // Call retry callback if provided
      if (onRetry) {
        onRetry(attempt + 1, maxRetries, currentDelay, error);
      }

      // Log retry attempt for debugging
      console.log(
        `Retry attempt ${attempt + 1}/${maxRetries} after ${currentDelay}ms`,
        error.message || error
      );

      // Wait before retrying
      await sleep(currentDelay);

      // Increase delay for next attempt
      delay *= backoffMultiplier;
    }
  }

  throw lastError;
}

/**
 * Sleep utility for delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wrapper for API calls that should retry on failure
 * @param {Function} apiCall - The API function to call
 * @param {Object} retryOptions - Retry configuration (optional)
 * @returns {Promise}
 */
export async function retryApiCall(apiCall, retryOptions = {}) {
  return retryWithBackoff(apiCall, {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
    ...retryOptions
  });
}
